import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  FamilyRestroom as FamilyIcon,
  Receipt as ReceiptIcon,
  ManageAccounts as PortalIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Warning as WarningIcon,
  ArrowUpward as UpgradeIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { subscriptionService, SUBSCRIPTION_PLANS, SUBSCRIPTION_STATUS } from '../services/subscriptionService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { primaryFamilyCodeFromUserDoc } from '../utils/familyScope';
import logger from '../utils/logger';

// ─── constants ────────────────────────────────────────────────────────────────

const planIcons = {
  free: null,
  individual: <StarIcon />,
  couple: <FavoriteIcon />,
  family: <FamilyIcon />,
};

const planColors = {
  free: 'inherit',
  individual: 'primary',
  couple: 'secondary',
  family: 'success',
};

const statusColors = {
  [SUBSCRIPTION_STATUS.ACTIVE]:     'success',
  [SUBSCRIPTION_STATUS.TRIALING]:   'info',
  [SUBSCRIPTION_STATUS.PAST_DUE]:   'warning',
  [SUBSCRIPTION_STATUS.CANCELED]:   'error',
  [SUBSCRIPTION_STATUS.SUSPENDED]:  'error',
  [SUBSCRIPTION_STATUS.INCOMPLETE]: 'warning',
};

// Which plan each plan can upgrade to (null = use add-on seats)
const UPGRADE_PATH = {
  free:       'individual',
  individual: 'couple',
  couple:     'family',
  family:     null,
};

// ─── component ────────────────────────────────────────────────────────────────

const SubscriptionManager = () => {
  const { currentUser, isTrialActive, trialDaysRemaining, isInGracePeriod, graceDaysRemaining } = useAuth();
  const { profile } = useProfile();
  const isMember = (profile?.role || currentUser?.role) === 'member';

  const [subscription, setSubscription] = useState(null);
  const [seatUsage, setSeatUsage]       = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [upgradeDialog, setUpgradeDialog] = useState(null); // plan object
  const [actionLoading, setActionLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [success, setSuccess] = useState(null);

  const userId     = currentUser?.uid;
  const familyCode = primaryFamilyCodeFromUserDoc(profile) || primaryFamilyCodeFromUserDoc(currentUser);

  const [members, setMembers] = useState([]);
  const [assignDialog, setAssignDialog] = useState(null); // { seatIndex }
  const [selectedMember, setSelectedMember] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  // ── handle return from Stripe checkout ──────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('checkout');
    if (status === 'success') {
      setSuccess('Subscription activated! Welcome to FamilyForLife.');
      // Strip query param without reload
      window.history.replaceState({}, '', window.location.pathname);
    } else if (status === 'cancel') {
      setError('Checkout was cancelled. Your plan has not changed.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // ── load subscription + seat data ────────────────────────────────────────
  useEffect(() => {
    if (!userId) { setLoading(false); return; }

    const load = async () => {
      try {
        const [sub, seats, history] = await Promise.all([
          subscriptionService.getSubscription(userId),
          familyCode ? subscriptionService.getSeatUsage(familyCode) : Promise.resolve(null),
          subscriptionService.getBillingHistory(userId),
        ]);
        setSubscription(sub);
        setSeatUsage(seats);
        setBillingHistory(history);
      } catch (err) {
        logger.error('Error loading subscription:', err);
        setError('Failed to load subscription details');
      } finally {
        setLoading(false);
      }

      // Load family members separately — rules may restrict non-owner users from
      // querying the users collection; a failure here must not surface as a
      // top-level "Failed to load subscription details" error.
      if (familyCode) {
        try {
          const snaps = await Promise.all([
            getDocs(query(collection(db, 'users'), where('familyCode', '==', familyCode))),
            getDocs(query(collection(db, 'users'), where('activeFamilyCode', '==', familyCode))),
            getDocs(query(collection(db, 'users'), where('currentFamily', '==', familyCode))),
          ]);
          const seen = new Set();
          const all = [];
          snaps.forEach(snap => snap.docs.forEach(d => {
            if (!seen.has(d.id)) { seen.add(d.id); all.push({ uid: d.id, ...d.data() }); }
          }));
          setMembers(all);
        } catch (err) {
          logger.error('Could not load family member list for seat display:', err);
          // Non-fatal — seat list will show UIDs instead of display names
        }
      }
    };

    load();
  }, [userId, familyCode]);

  // ── upgrade to a plan via Stripe Checkout ────────────────────────────────
  const handleUpgrade = async (planId) => {
    setActionLoading(true);
    setError(null);
    try {
      const { url } = await subscriptionService.createCheckoutSession(userId, planId);
      window.location.href = url;
    } catch (err) {
      logger.error('Checkout error:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setActionLoading(false);
    }
    // No finally — if redirect succeeds the component unmounts
    setUpgradeDialog(null);
  };

  // ── open Stripe Customer Portal ──────────────────────────────────────────
  const handleManageBilling = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      const { url } = await subscriptionService.createPortalSession(userId);
      window.location.href = url;
    } catch (err) {
      logger.error('Portal error:', err);
      setError(err.message || 'Failed to open billing portal. Please try again.');
      setPortalLoading(false);
    }
  };

  // ── assign a seat to a family member ────────────────────────────────────
  const handleAssignSeat = async () => {
    if (!selectedMember || !familyCode) return;
    setAssignLoading(true);
    setError(null);
    try {
      const result = await subscriptionService.assignSeat(familyCode, selectedMember);
      if (result.success) {
        setSuccess('Seat assigned.');
        setAssignDialog(null);
        setSelectedMember('');
        const seats = await subscriptionService.getSeatUsage(familyCode);
        setSeatUsage(seats);
      } else {
        setError(result.reason || 'Could not assign seat.');
      }
    } catch (err) {
      logger.error('assignSeat error:', err);
      setError(err.message || 'Failed to assign seat.');
    } finally {
      setAssignLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Members are covered by the family owner's plan via seat assignment.
  // Show a simple informational view rather than the full subscription manager.
  const hasSeat = seatUsage?.seats?.some(s => s.assignedTo === userId && s.status === 'active');
  if (isMember && hasSeat) {
    const planLabel = seatUsage
      ? (Object.values(SUBSCRIPTION_PLANS).find(p => p.id === seatUsage.planId)?.name || 'Family')
      : 'Family';
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FamilyIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
          <Typography variant="h5" gutterBottom>Your Access</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            You are a member of a <strong>{planLabel} Plan</strong> family group.
            Your access is provided by your family's subscription — no separate billing is needed.
          </Typography>
          <Chip label="Active Member" color="success" sx={{ mb: 3 }} />
          <Typography variant="body2" color="text.secondary">
            Contact your family administrator to make changes to the subscription.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const currentPlanId   = subscription?.planId || 'free';
  const currentPlan     = Object.values(SUBSCRIPTION_PLANS).find(p => p.id === currentPlanId) || SUBSCRIPTION_PLANS.FREE;
  const upgradeTargetId = UPGRADE_PATH[currentPlanId];
  const upgradeTarget   = upgradeTargetId
    ? Object.values(SUBSCRIPTION_PLANS).find(p => p.id === upgradeTargetId)
    : null;

  const seatsUsed  = seatUsage?.usedSeats  ?? 0;
  const seatsTotal = seatUsage?.totalSeats ?? 0;
  const seatPct    = seatsTotal > 0 ? (seatsUsed / seatsTotal) * 100 : 0;
  const seatsAvail = Math.max(0, seatsTotal - seatsUsed);
  const atSeatLimit = seatsTotal > 0 && seatsUsed >= seatsTotal;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

      {/* ── alerts ── */}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {isInGracePeriod && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <AlertTitle>Payment issue — {graceDaysRemaining} day{graceDaysRemaining !== 1 ? 's' : ''} to resolve</AlertTitle>
          Your last payment failed. Update your payment method to avoid losing access.
          <Button size="small" variant="outlined" color="warning" sx={{ ml: 2 }} onClick={handleManageBilling}>
            Update Payment
          </Button>
        </Alert>
      )}

      {isTrialActive && (
        <Alert severity="info" icon={<StarIcon />} sx={{ mb: 2 }}>
          <AlertTitle>
            Free trial — {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} remaining
          </AlertTitle>
          Subscribe before your trial ends to keep full access to all features.
        </Alert>
      )}

      {subscription?.stripeCustomerId &&
        !subscriptionService.hasActiveStripeSubscription(subscription) &&
        !subscriptionService.isInGracePeriod(subscription) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Start your paid plan in the app</AlertTitle>
          The Stripe <strong>Customer Portal</strong> only updates cards and billing details — it does not charge
          for a new subscription. Use <strong>Subscribe</strong> on your plan below to open{' '}
          <strong>Stripe Checkout</strong> and complete payment.
        </Alert>
      )}

      {/* ── current plan summary ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" gutterBottom>Subscription</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body1">
                Current plan: <strong>{currentPlan.name}</strong>
              </Typography>
              <Chip
                label={subscription?.status?.replace('_', ' ').toUpperCase() || 'FREE'}
                color={statusColors[subscription?.status] || 'default'}
                size="small"
              />
            </Box>
            {subscription?.currentPeriodEnd && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subscription.cancelAtPeriodEnd
                  ? `Access until ${new Date(subscription.currentPeriodEnd?.seconds * 1000 || subscription.currentPeriodEnd).toLocaleDateString()}`
                  : `Next billing: ${new Date(subscription.currentPeriodEnd?.seconds * 1000 || subscription.currentPeriodEnd).toLocaleDateString()}`}
              </Typography>
            )}
          </Box>
          {subscription?.stripeCustomerId && (
            <Button
              variant="outlined"
              startIcon={portalLoading ? <CircularProgress size={16} /> : <PortalIcon />}
              onClick={handleManageBilling}
              disabled={portalLoading}
            >
              {subscriptionService.hasActiveStripeSubscription(subscription) || subscriptionService.isInGracePeriod(subscription)
                ? 'Manage Billing'
                : 'Payment methods'}
            </Button>
          )}
        </Box>

        {/* seat usage bar */}
        {seatsTotal > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                <strong>{seatsUsed} of {seatsTotal}</strong> seats used
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {seatsAvail} available
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={seatPct}
              color={atSeatLimit ? 'error' : seatPct > 75 ? 'warning' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />

            {/* seat owner list */}
            {seatUsage?.seats?.length > 0 && (() => {
              const memberById = (uid) => members.find(m => m.uid === uid);
              const unassignedMembers = members.filter(
                m => !seatUsage.seats.some(s => s.assignedTo === m.uid && s.status === 'active')
              );
              return (
                <Box sx={{ mt: 1.5 }}>
                  {seatUsage.seats.map((seat, i) => {
                    const isActive = seat.status === 'active' && !!seat.assignedTo;
                    const member = seat.assignedTo ? memberById(seat.assignedTo) : null;
                    const displayName = seat.assignedTo === userId
                      ? 'You'
                      : member
                        ? (`${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email)
                        : seat.assignedTo || null;
                    return (
                      <Box key={seat.id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {isActive
                          ? <PersonIcon fontSize="small" color="success" />
                          : <PersonAddIcon fontSize="small" color="disabled" />}
                        <Typography variant="caption" color={isActive ? 'text.primary' : 'text.disabled'} sx={{ flex: 1 }}>
                          Seat {i + 1} — {isActive ? displayName : 'Unassigned'}
                        </Typography>
                        {!isActive && unassignedMembers.length > 0 && (
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<PersonAddIcon />}
                            sx={{ fontSize: '0.7rem', py: 0 }}
                            onClick={() => { setAssignDialog({ seatIndex: i }); setSelectedMember(''); }}
                          >
                            Assign
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}

            {/* at limit — show upgrade CTA */}
            {atSeatLimit && upgradeTarget && (
              <Alert severity="info" sx={{ mt: 2 }} icon={<UpgradeIcon />}>
                All seats are used. Upgrade to <strong>{upgradeTarget.name}</strong> for {upgradeTarget.seats} seats.
                <Button size="small" variant="contained" sx={{ ml: 2 }} onClick={() => setUpgradeDialog(upgradeTarget)}>
                  Upgrade
                </Button>
              </Alert>
            )}
            {atSeatLimit && !upgradeTarget && currentPlanId === 'family' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                All seats are used. Add additional member seats from the Manage Billing portal.
                <Button size="small" variant="outlined" sx={{ ml: 2 }} onClick={handleManageBilling}>
                  Add Seats
                </Button>
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* ── plan cards ── */}
      <Typography variant="h6" gutterBottom>Plans</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {Object.values(SUBSCRIPTION_PLANS).map((plan) => {
          const isCurrent  = plan.id === currentPlanId;
          const isUpgrade  = plan.price > currentPlan.price;
          const isDowngrade = plan.price < currentPlan.price && plan.id !== 'free';

          return (
            <Grid item xs={12} sm={6} md={3} key={plan.id}>
              <Card
                variant={isCurrent ? 'elevation' : 'outlined'}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: isCurrent ? 2 : 1,
                  borderColor: isCurrent ? `${planColors[plan.id]}.main` : 'divider',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {planIcons[plan.id]}
                    <Typography variant="h6" sx={{ ml: planIcons[plan.id] ? 1 : 0 }}>
                      {plan.name}
                    </Typography>
                    {isCurrent && <Chip label="Current" size="small" color="primary" sx={{ ml: 1 }} />}
                  </Box>
                  <Typography variant="h4" gutterBottom>
                    {plan.price === 0 ? 'Free' : `$${(plan.price / 100).toFixed(2)}`}
                    {plan.interval && (
                      <Typography variant="body2" component="span" color="text.secondary">
                        /mo
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {plan.seats === 0 ? 'No paid seats' : `${plan.seats} seat${plan.seats !== 1 ? 's' : ''} included`}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <List dense>
                    {plan.features.slice(0, 6).map((f) => (
                      <ListItem disablePadding key={f}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText primary={f.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  {isCurrent ? (
                    plan.id !== 'free' ? (
                      subscriptionService.isInGracePeriod(subscription) ? (
                        <Button
                          variant="contained"
                          color="warning"
                          fullWidth
                          onClick={handleManageBilling}
                          disabled={portalLoading}
                        >
                          {portalLoading ? <CircularProgress size={20} /> : 'Update payment'}
                        </Button>
                      ) : subscriptionService.hasActiveStripeSubscription(subscription) ? (
                        <Button variant="outlined" fullWidth onClick={handleManageBilling} disabled={portalLoading}>
                          {portalLoading ? <CircularProgress size={20} /> : 'Manage'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color={planColors[plan.id]}
                          fullWidth
                          onClick={() => setUpgradeDialog(plan)}
                          startIcon={<UpgradeIcon />}
                        >
                          Subscribe
                        </Button>
                      )
                    ) : null
                  ) : isUpgrade ? (
                    <Button
                      variant="contained"
                      color={planColors[plan.id]}
                      fullWidth
                      onClick={() => setUpgradeDialog(plan)}
                      startIcon={<UpgradeIcon />}
                    >
                      Upgrade
                    </Button>
                  ) : isDowngrade && subscriptionService.hasActiveStripeSubscription(subscription) ? (
                    <Tooltip title="Downgrade available via Manage Billing">
                      <Button variant="outlined" color="inherit" fullWidth onClick={handleManageBilling}>
                        Switch
                      </Button>
                    </Tooltip>
                  ) : null}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* ── billing history ── */}
      {billingHistory.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ReceiptIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Billing History</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Invoice</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingHistory.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>
                      {inv.createdAt?.toDate
                        ? inv.createdAt.toDate().toLocaleDateString()
                        : inv.createdAt
                          ? new Date(inv.createdAt?.seconds * 1000 || inv.createdAt).toLocaleDateString()
                          : 'N/A'}
                    </TableCell>
                    <TableCell>{inv.description || 'Subscription payment'}</TableCell>
                    <TableCell align="right">${((inv.amount || 0) / 100).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip label={inv.status || 'paid'} size="small" color={inv.status === 'paid' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>
                      {inv.hostedInvoiceUrl && (
                        <Button size="small" href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── upgrade confirmation dialog ── */}
      <Dialog open={!!upgradeDialog} onClose={() => !actionLoading && setUpgradeDialog(null)}>
        <DialogTitle>
          {upgradeDialog?.id === currentPlanId ? `Subscribe to ${upgradeDialog?.name}` : `Upgrade to ${upgradeDialog?.name}`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {upgradeDialog?.id === currentPlanId
              ? `Convert your free trial to a paid ${upgradeDialog?.name} plan at `
              : `You are upgrading to the ${upgradeDialog?.name} plan at `}
            <strong>${upgradeDialog ? (upgradeDialog.price / 100).toFixed(2) : '0.00'}/mo</strong>.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            Includes <strong>{upgradeDialog?.seats} seat{upgradeDialog?.seats !== 1 ? 's' : ''}</strong>.
            You will be redirected to Stripe to complete payment.
          </DialogContentText>
          {isTrialActive && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Your free trial is active. Billing begins after you complete checkout.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpgradeDialog(null)} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleUpgrade(upgradeDialog.id)}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <UpgradeIcon />}
          >
            {actionLoading ? 'Redirecting…' : 'Continue to Checkout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── assign seat dialog ── */}
      <Dialog open={!!assignDialog} onClose={() => !assignLoading && setAssignDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Seat {assignDialog?.seatIndex != null ? assignDialog.seatIndex + 1 : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a family member to assign this seat to. They will gain full access based on your plan.
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel>Select Member</InputLabel>
            <Select
              value={selectedMember}
              label="Select Member"
              onChange={e => setSelectedMember(e.target.value)}
            >
              {members
                .filter(m => !seatUsage?.seats?.some(s => s.assignedTo === m.uid && s.status === 'active'))
                .map(m => (
                  <MenuItem key={m.uid} value={m.uid}>
                    {`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email} — {m.email}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(null)} disabled={assignLoading}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedMember || assignLoading}
            onClick={handleAssignSeat}
          >
            {assignLoading ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SubscriptionManager;
